import React, { useState } from 'react';
import { func, shape, arrayOf, string } from 'prop-types';

import Error from 'components/Error';
import Close from 'components/Icons/Close';
import { GET_USER_RECIPE_BOX, GET_USER_RECIPE_TAGS } from 'graphql/recipebox';
import styles, { userTagsStyles, addNewTagsFormStyles } from './styles';

export function ManageRecipeTagsModal(props) {
  const {
    userRecipeId,
    allUserTags,
    recipeTags,
    recipeTagsIds,
    toggleDialog,
    onRecipeBoxDataChange,
    onUpdate,
    onCreateUserRecipeTags,
    onUpdateRecipeInBox
  } = props;

  const [selectedItems, setSelectedItems] = useState(new Set(recipeTags));
  const [selectedItemsIds, setSelectedItemsIds] = useState(
    new Set(recipeTagsIds)
  );
  const [inputString, setInputString] = useState('');
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function handleItemSelection(item) {
    const selected = new Set(selectedItems);
    if (selectedItems.has(item)) {
      selected.delete(item);
    } else {
      selected.add(item);
    }
    setSelectedItems(selected);
  }

  function handleItemIdsSelection(item) {
    const selected = new Set(selectedItemsIds);
    if (selectedItemsIds.has(item)) {
      selected.delete(item);
    } else {
      selected.add(item);
    }
    setSelectedItemsIds(selected);
  }

  function handleUpdate(tagsIds) {
    const updateCache = (cache, { data: { updateRecipeInBox } }) => {
      try {
        const rb = cache.readQuery({
          query: GET_USER_RECIPE_BOX
        });
        cache.writeQuery({
          query: GET_USER_RECIPE_BOX,
          data: {
            recipeBox: {
              ...rb.recipeBox,
              recipes: [
                ...rb.recipeBox.recipes.filter(
                  r => r.userRecipeId.toString() !== userRecipeId.toString()
                ),
                updateRecipeInBox.userRecipe
              ]
            }
          }
        });
      } catch (err) {
        // nothing to do here, sadly.
      }
    };
    onUpdateRecipeInBox({
      variables: {
        input: {
          userRecipeId,
          tagsIds
        }
      },
      update: updateCache
    })
      .then((_, onUpdateError) => {
        if (onUpdateError) {
          setError(onUpdateError);
          return;
        }
        setIsSubmitting(false);
        setInputString('');
        onRecipeBoxDataChange(1500);
        // This is just to display a message box on the Teaser
        onUpdate();
        toggleDialog();
      })
      .catch(onUpdateError => setError(onUpdateError));
  }

  function renderUserTags() {
    return (
      <>
        <div className="UserTags">
          {allUserTags.map((item, idx) => {
            const inputId = `${item.name}-input-${idx}`;
            return (
              <div key={item.recipeTagId} className="UserTags__itemContainer">
                <label
                  className={`UserTags__item ${
                    selectedItems.has(item.name)
                      ? 'UserTags__item--selected'
                      : ''
                  }`}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleItemSelection(item.name);
                    handleItemIdsSelection(item.recipeTagId);
                  }}
                  htmlFor={inputId}
                >
                  <input type="checkbox" id={inputId} />
                  {item.name}
                </label>
              </div>
            );
          })}
        </div>
        <style jsx>{userTagsStyles}</style>
      </>
    );
  }

  function renderAddNewTagsForm() {
    return (
      <form
        className="ManageRecipeTagsModal__addNewTagsForm"
        onSubmit={e => {
          e.preventDefault();
          setIsSubmitting(true);
          if (inputString.length > 0) {
            const updateCache = (cache, { data: { createRecipeTags } }) => {
              try {
                const rb = cache.readQuery({
                  query: GET_USER_RECIPE_BOX
                });
                cache.writeQuery({
                  query: GET_USER_RECIPE_BOX,
                  data: {
                    recipeBox: {
                      ...rb.recipeBox,
                      tags: [
                        ...rb.recipeBox.tags,
                        ...createRecipeTags.recipeTags
                      ]
                    }
                  }
                });

                const rt = cache.readQuery({
                  query: GET_USER_RECIPE_TAGS
                });
                cache.writeQuery({
                  query: GET_USER_RECIPE_TAGS,
                  data: {
                    recipeTags: [
                      ...rt.recipeTags,
                      ...createRecipeTags.recipeTags
                    ]
                  }
                });
              } catch (err) {
                // nothing to do here, sadly.
              }
            };
            // Create new tags first
            onCreateUserRecipeTags({
              variables: {
                input: {
                  names: [
                    ...new Set(inputString.split(',').map(item => item.trim()))
                  ]
                }
              },
              update: updateCache
            })
              .then(({ data }, onCreateError) => {
                if (onCreateError) {
                  setError(onCreateError);
                  return;
                }
                // And finally add the newly created tags (plus the selected ones) to the current Recipe
                handleUpdate([
                  ...selectedItemsIds,
                  ...data.createRecipeTags.recipeTags.reduce((acc, cur) => {
                    acc.push(cur.recipeTagId);
                    return acc;
                  }, [])
                ]);
              })
              .catch(onCreateError => setError(onCreateError));
          } else {
            // No new Tags to create
            handleUpdate([...selectedItemsIds]);
          }
        }}
      >
        <h4>Add Tags</h4>
        <span>Add as many tags as you like</span>
        <div className="ManageRecipeTagsModal__inputContainer">
          <label
            className="ManageRecipeTagsModal__inputWrapper"
            htmlFor="ManageRecipeTagsModal__tagsInput"
          >
            <input
              className="ManageRecipeTagsModal__tagsInput"
              id="ManageRecipeTagsModal__tagsInput"
              placeholder=" "
              value={inputString}
              onChange={e => setInputString(e.target.value)}
            />
            <div className="ManageRecipeTagsModal__labelText">
              e.g. Birthday party, Afterschool
            </div>
          </label>
          <div className="ManageRecipeTagsModal__actions">
            <button
              className={`button ManageRecipeTagsModal__submitButton ${
                isSubmitting
                  ? 'ManageRecipeTagsModal__submitButton--isSubmitting'
                  : ''
              }`}
              type="submit"
              required
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
        <style jsx>{addNewTagsFormStyles}</style>
      </form>
    );
  }

  return (
    <div className="ManageRecipeTagsModal">
      <div className="ManageRecipeTagsModal__currentTags">
        <h4>Select Tags</h4>
        {renderUserTags()}
        {allUserTags.length === 0 && (
          <p>
            There&apos;s no Tags yet. Use the below form to create tags to
            organize your Recipes
          </p>
        )}
        {error && <Error message={error.message} />}
      </div>
      {renderAddNewTagsForm()}
      <div className="ManageRecipeTagsModal__close">
        <a href="#" aria-label="Close Dialog" onClick={() => toggleDialog()}>
          <Close />
        </a>
      </div>
      <style jsx>{styles}</style>
    </div>
  );
}

ManageRecipeTagsModal.propTypes = {
  userRecipeId: string.isRequired,
  allUserTags: arrayOf(shape({})).isRequired,
  recipeTags: arrayOf(string),
  recipeTagsIds: arrayOf(string),
  toggleDialog: func.isRequired,
  onRecipeBoxDataChange: func.isRequired,
  onUpdate: func.isRequired,
  onCreateUserRecipeTags: func.isRequired,
  onUpdateRecipeInBox: func.isRequired
};

ManageRecipeTagsModal.defaultProps = {
  recipeTags: [],
  recipeTagsIds: []
};

export default ManageRecipeTagsModal;
